export interface SessionCacheIdentity {
    isLoggedIn: boolean
    userId?: number
    dealershipId?: number
}

export interface SessionCacheBoundary {
    getSnapshot: () => number
    subscribe: (listener: () => void) => () => void
    dispose: () => void
}

// Observe every authentication transition at its source. React can batch a
// logout and login into one render with the same final authentication state.
export function createSessionCacheBoundary(
    getIdentity: () => SessionCacheIdentity,
    subscribeToIdentity: (listener: () => void) => () => void,
): SessionCacheBoundary {
    let identity = getIdentity()
    let generation = 0
    const listeners = new Set<() => void>()

    const unsubscribe = subscribeToIdentity(() => {
        const next = getIdentity()

        const changedUser =
            identity.userId !== undefined &&
            next.userId !== undefined &&
            identity.userId !== next.userId

        const changedDealership =
            identity.dealershipId !== undefined &&
            next.dealershipId !== undefined &&
            identity.dealershipId !== next.dealershipId

        const changedBoundary =
            identity.isLoggedIn !== next.isLoggedIn ||
            (next.isLoggedIn && (changedUser || changedDealership))

        // Resolving an identity after login does not start another session.
        // Retain known identities during a same-session user reload.
        if (next.isLoggedIn && identity.isLoggedIn) {
            identity = {
                ...next,
                userId: next.userId ?? identity.userId,
                dealershipId: next.dealershipId ?? identity.dealershipId,
            }
        } else {
            identity = next
        }

        if (!changedBoundary) return

        generation += 1
        for (const listener of listeners) listener()
    })

    return {
        getSnapshot: () => generation,
        subscribe: (listener) => {
            listeners.add(listener)

            return () => {
                listeners.delete(listener)
            }
        },
        dispose: () => {
            unsubscribe()
            listeners.clear()
        },
    }
}
